import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BookOpen, FilePdf, FileImage, FileHtml, Eye, Gear, MagnifyingGlass, PaperPlaneTilt } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { compressImage } from '@/lib/imageCompress';
import { useDataCache } from '@/contexts/DataCache';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Catalog = () => {
  const { products, categories, sellers } = useDataCache();
  const [tab, setTab] = useState('generate'); // generate | template
  const [generating, setGenerating] = useState(false);

  // Envío Telegram
  const [tgOpen, setTgOpen] = useState(false);
  const [tgSellerId, setTgSellerId] = useState('');
  const [tgFormat, setTgFormat] = useState('pdf');
  const [tgMessage, setTgMessage] = useState('');
  const [tgSending, setTgSending] = useState(false);
  // Envío Existencias
  const [stOpen, setStOpen] = useState(false);
  const [stSellerId, setStSellerId] = useState('');
  const [stMessage, setStMessage] = useState('');
  const [stSending, setStSending] = useState(false);

  // Generación
  const [layout, setLayout] = useState('grid');
  const [rows, setRows] = useState(4);
  const [cols, setCols] = useState(5);
  const [perPage, setPerPage] = useState(30);

  // Filtros
  const [selectionMode, setSelectionMode] = useState('all'); // all | ids
  const [categoryIds, setCategoryIds] = useState(new Set());
  const [subcategoryIds, setSubcategoryIds] = useState(new Set());
  const [brand, setBrand] = useState('all');
  const [search, setSearch] = useState('');
  const [onlyStock, setOnlyStock] = useState(false);
  const [onlyPromo, setOnlyPromo] = useState(false);
  const [onlyImage, setOnlyImage] = useState(false);
  const [groupByCategory, setGroupByCategory] = useState(true);
  const [showSubcatHeaders, setShowSubcatHeaders] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [pickerSearch, setPickerSearch] = useState('');

  // Campos
  const [fields, setFields] = useState({
    show_image: true,
    show_name: true,
    show_price: true,
    show_stock: false,
    show_brand: true,
    show_variant: true,
    show_sku: false,
    show_promo: true,
    price_field: 'precio_1'
  });

  // Plantilla
  const [tpl, setTpl] = useState({
    title: '', footer_text: '', logo_url: '',
    primary_color: '#2563eb', secondary_color: '#0f172a',
    background_color: '#ffffff', text_color: '#0f172a'
  });

  useEffect(() => {
    axios.get(`${API}/catalog/template`, { withCredentials: true })
      .then((r) => setTpl(r.data))
      .catch(() => {});
  }, []);

  const parentCategories = useMemo(
    () => categories.filter(c => !c.parent_id).slice().sort((a, b) => a.name.localeCompare(b.name)),
    [categories]
  );
  const availableSubcategories = useMemo(() => {
    if (categoryIds.size === 0) {
      return categories.filter(c => c.parent_id).slice().sort((a, b) => a.name.localeCompare(b.name));
    }
    return categories
      .filter(c => c.parent_id && categoryIds.has(c.parent_id))
      .slice().sort((a, b) => a.name.localeCompare(b.name));
  }, [categories, categoryIds]);
  const brands = useMemo(
    () => Array.from(new Set(products.filter(p => !p.parent_product_id && p.brand).map(p => p.brand))).sort(),
    [products]
  );

  const toggleCategory = (id) => {
    const s = new Set(categoryIds);
    if (s.has(id)) {
      s.delete(id);
      // Limpiar subcategorías huérfanas
      const newSubs = new Set([...subcategoryIds].filter(sid => {
        const sub = categories.find(c => c.id === sid);
        return sub && s.has(sub.parent_id);
      }));
      setSubcategoryIds(newSubs);
    } else {
      s.add(id);
    }
    setCategoryIds(s);
  };
  const toggleSubcategory = (id) => {
    const s = new Set(subcategoryIds);
    s.has(id) ? s.delete(id) : s.add(id);
    setSubcategoryIds(s);
  };

  // Productos para el picker manual
  const pickerProducts = useMemo(() => {
    return products.filter(p => {
      if (p.has_variants) return false;
      const t = pickerSearch.toLowerCase();
      if (t && !p.name.toLowerCase().includes(t) && !(p.sku || '').toLowerCase().includes(t)) return false;
      return true;
    }).slice(0, 100);
  }, [products, pickerSearch]);

  const toggleSelect = (id) => {
    const s = new Set(selectedIds);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelectedIds(s);
  };

  const buildPayload = (format) => ({
    format,
    layout,
    rows: parseInt(rows) || 4,
    cols: parseInt(cols) || 5,
    products_per_page: parseInt(perPage) || 30,
    fields,
    selection_mode: selectionMode,
    product_ids: selectionMode === 'ids' ? Array.from(selectedIds) : [],
    category_ids: Array.from(categoryIds),
    subcategory_ids: Array.from(subcategoryIds),
    brand: brand !== 'all' ? brand : null,
    search: search || null,
    only_with_stock: onlyStock,
    only_with_promo: onlyPromo,
    only_with_image: onlyImage,
    group_by_category: groupByCategory,
    show_subcategory_headers: showSubcatHeaders
  });

  const downloadFile = async (format) => {
    setGenerating(true);
    try {
      const payload = buildPayload(format);
      const res = await axios.post(`${API}/catalog/generate`, payload, {
        withCredentials: true,
        responseType: 'blob'
      });
      const blob = new Blob([res.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext = format === 'pdf' ? 'pdf' : (res.data.type === 'application/zip' ? 'zip' : 'png');
      a.download = `catalogo.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Catálogo descargado');
    } catch (e) {
      toast.error('Error al generar catálogo');
    } finally { setGenerating(false); }
  };

  const linkedSellers = useMemo(
    () => sellers.filter(s => s.telegram_chat_id && s.active !== false).slice().sort((a, b) => a.name.localeCompare(b.name)),
    [sellers]
  );

  const openTelegramDialog = () => {
    if (linkedSellers.length === 0) {
      toast.error('No hay vendedores con Telegram vinculado');
      return;
    }
    setTgSellerId(linkedSellers[0]?.id || '');
    setTgFormat('pdf');
    setTgMessage('');
    setTgOpen(true);
  };

  const sendCatalogTelegram = async () => {
    if (!tgSellerId) { toast.error('Selecciona un vendedor'); return; }
    setTgSending(true);
    try {
      const payload = { ...buildPayload(tgFormat), seller_id: tgSellerId, message: tgMessage || null };
      const { data } = await axios.post(`${API}/catalog/send-telegram`, payload, { withCredentials: true });
      toast.success(`Catálogo enviado a ${data.seller_name}`);
      setTgOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al enviar por Telegram');
    } finally {
      setTgSending(false);
    }
  };

  const openStockDialog = () => {
    if (linkedSellers.length === 0) {
      toast.error('No hay vendedores con Telegram vinculado');
      return;
    }
    setStSellerId(linkedSellers[0]?.id || '');
    setStMessage('');
    setStOpen(true);
  };

  const sendStockTelegram = async () => {
    if (!stSellerId) { toast.error('Selecciona un vendedor'); return; }
    setStSending(true);
    try {
      const payload = { ...buildPayload('pdf'), seller_id: stSellerId, message: stMessage || null };
      const { data } = await axios.post(`${API}/catalog/send-stock-telegram`, payload, { withCredentials: true });
      toast.success(`Existencias enviadas a ${data.seller_name}`);
      setStOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al enviar existencias');
    } finally {
      setStSending(false);
    }
  };

  const previewHTML = async () => {
    setGenerating(true);
    try {
      const payload = buildPayload('html');
      const res = await axios.post(`${API}/catalog/generate`, payload, {
        withCredentials: true,
        responseType: 'text'
      });
      const w = window.open('', '_blank');
      if (w) {
        w.document.open();
        w.document.write(res.data);
        w.document.close();
      } else {
        toast.error('Tu navegador bloqueó la ventana emergente.');
      }
    } catch (e) {
      toast.error('Error al generar vista previa');
    } finally { setGenerating(false); }
  };

  const saveTemplate = async () => {
    try {
      await axios.put(`${API}/catalog/template`, tpl, { withCredentials: true });
      toast.success('Plantilla guardada');
    } catch (e) { toast.error('Error al guardar'); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await compressImage(file, { maxDim: 800, targetBytes: 300 * 1024 });
      setTpl({ ...tpl, logo_url: dataUrl });
    } catch (err) {
      toast.error(err.message || 'No se pudo procesar la imagen');
    } finally {
      e.target.value = '';
    }
  };

  return (
    <MainLayout>
      <div className="p-8">
        <div className="flex items-center gap-3 mb-6">
          <BookOpen size={32} weight="bold" className="text-violet-600" />
          <div>
            <h1 className="text-4xl font-bold tracking-tight">Catálogo</h1>
            <p className="text-muted-foreground">Genera catálogos en PDF, HTML o PNG con tu plantilla personalizada</p>
          </div>
        </div>

        <div className="bg-slate-100 p-1 rounded-full text-xs flex gap-1 w-fit mb-4">
          <button onClick={() => setTab('generate')} className={`px-4 py-1.5 rounded-full ${tab === 'generate' ? 'bg-white shadow-sm font-semibold' : 'text-muted-foreground'}`} data-testid="tab-generate">
            <BookOpen size={14} weight="bold" className="inline mr-1" /> Generar
          </button>
          <button onClick={() => setTab('template')} className={`px-4 py-1.5 rounded-full ${tab === 'template' ? 'bg-white shadow-sm font-semibold' : 'text-muted-foreground'}`} data-testid="tab-template">
            <Gear size={14} weight="bold" className="inline mr-1" /> Plantilla
          </button>
        </div>

        {tab === 'generate' ? (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            {/* Filtros */}
            <div className="lg:col-span-4 bg-white border rounded p-4 space-y-3">
              <h3 className="font-semibold text-lg">Filtros</h3>

              <div>
                <Label className="text-xs">Selección</Label>
                <Select value={selectionMode} onValueChange={setSelectionMode}>
                  <SelectTrigger data-testid="cat-selection-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los productos (con filtros)</SelectItem>
                    <SelectItem value="ids">Selección manual</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {selectionMode === 'all' && (
                <>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-xs">Categorías</Label>
                      {categoryIds.size > 0 && (
                        <button onClick={() => { setCategoryIds(new Set()); setSubcategoryIds(new Set()); }}
                                className="text-xs text-violet-600 hover:underline" data-testid="cat-clear-cats">
                          Limpiar ({categoryIds.size})
                        </button>
                      )}
                    </div>
                    <div className="max-h-[160px] overflow-y-auto border rounded p-2 space-y-1 text-sm bg-slate-50">
                      {parentCategories.length === 0 && (
                        <p className="text-xs text-muted-foreground">Sin categorías</p>
                      )}
                      {parentCategories.map(c => (
                        <label key={c.id} className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded">
                          <input
                            type="checkbox"
                            checked={categoryIds.has(c.id)}
                            onChange={() => toggleCategory(c.id)}
                            data-testid={`cat-cat-${c.id}`}
                          />
                          <span>{c.name}</span>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {categoryIds.size === 0 ? 'Vacío = todas las categorías' : `${categoryIds.size} categoría${categoryIds.size === 1 ? '' : 's'} seleccionada${categoryIds.size === 1 ? '' : 's'}`}
                    </p>
                  </div>
                  {availableSubcategories.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label className="text-xs">Subcategorías</Label>
                        {subcategoryIds.size > 0 && (
                          <button onClick={() => setSubcategoryIds(new Set())}
                                  className="text-xs text-violet-600 hover:underline">
                            Limpiar ({subcategoryIds.size})
                          </button>
                        )}
                      </div>
                      <div className="max-h-[140px] overflow-y-auto border rounded p-2 space-y-1 text-sm bg-slate-50">
                        {availableSubcategories.map(c => {
                          const parent = categories.find(p => p.id === c.parent_id);
                          return (
                            <label key={c.id} className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded">
                              <input
                                type="checkbox"
                                checked={subcategoryIds.has(c.id)}
                                onChange={() => toggleSubcategory(c.id)}
                                data-testid={`cat-sub-${c.id}`}
                              />
                              <span className="flex-1">{c.name}</span>
                              {parent && categoryIds.size === 0 && (
                                <span className="text-xs text-muted-foreground">{parent.name}</span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div>
                    <Label className="text-xs">Marca</Label>
                    <Select value={brand} onValueChange={setBrand}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        {brands.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Búsqueda libre</Label>
                    <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nombre, SKU…" />
                  </div>
                  <div className="space-y-1 pt-2 border-t text-sm">
                    <label className="flex items-center gap-2"><input type="checkbox" checked={onlyStock} onChange={(e) => setOnlyStock(e.target.checked)} /> Sólo con stock</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={onlyPromo} onChange={(e) => setOnlyPromo(e.target.checked)} /> Sólo con promoción</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={onlyImage} onChange={(e) => setOnlyImage(e.target.checked)} /> Sólo con imagen</label>
                  </div>
                  <div className="space-y-1 pt-2 border-t text-sm">
                    <label className="flex items-center gap-2" data-testid="cat-group-by">
                      <input type="checkbox" checked={groupByCategory} onChange={(e) => setGroupByCategory(e.target.checked)} />
                      <span className="font-medium">Agrupar por categoría</span>
                    </label>
                    {groupByCategory && (
                      <label className="flex items-center gap-2 ml-6 text-muted-foreground">
                        <input type="checkbox" checked={showSubcatHeaders} onChange={(e) => setShowSubcatHeaders(e.target.checked)} />
                        Mostrar encabezados de subcategoría
                      </label>
                    )}
                  </div>
                </>
              )}

              {selectionMode === 'ids' && (
                <div>
                  <div className="relative mb-2">
                    <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input className="pl-7 h-8 text-xs" placeholder="Buscar..." value={pickerSearch} onChange={(e) => setPickerSearch(e.target.value)} />
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">Seleccionados: <strong>{selectedIds.size}</strong></p>
                  <div className="max-h-[280px] overflow-y-auto border rounded space-y-0.5 p-1">
                    {pickerProducts.map(p => (
                      <label key={p.id} className="flex items-center gap-2 p-1 hover:bg-slate-50 cursor-pointer text-xs rounded">
                        <input type="checkbox" checked={selectedIds.has(p.id)} onChange={() => toggleSelect(p.id)} />
                        {p.image_url && <img src={p.image_url} alt="" className="w-6 h-6 object-cover rounded" />}
                        <span className="flex-1 truncate">{p.name}{p.variant_name ? ` (${p.variant_name})` : ''}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Layout + Campos */}
            <div className="lg:col-span-4 bg-white border rounded p-4 space-y-3">
              <h3 className="font-semibold text-lg">Diseño</h3>
              <div>
                <Label className="text-xs">Tipo de presentación</Label>
                <Select value={layout} onValueChange={setLayout}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="grid">Cuadrícula con imágenes</SelectItem>
                    <SelectItem value="list">Lista de precios (sin imagen)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {layout === 'grid' ? (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Filas</Label>
                    <Input type="number" min="1" max="12" value={rows} onChange={(e) => setRows(e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-xs">Columnas</Label>
                    <Input type="number" min="1" max="8" value={cols} onChange={(e) => setCols(e.target.value)} />
                  </div>
                  <p className="col-span-2 text-xs text-muted-foreground">
                    Total por página: <strong>{(parseInt(rows) || 0) * (parseInt(cols) || 0)}</strong>
                  </p>
                </div>
              ) : (
                <div>
                  <Label className="text-xs">Productos por página</Label>
                  <Input type="number" min="10" max="80" value={perPage} onChange={(e) => setPerPage(e.target.value)} />
                </div>
              )}

              <div className="border-t pt-3">
                <Label className="text-xs font-semibold mb-1 block">Mostrar</Label>
                <div className="grid grid-cols-2 gap-1 text-xs">
                  {[
                    ['show_image', 'Imagen'],
                    ['show_name', 'Nombre'],
                    ['show_variant', 'Variante / color'],
                    ['show_brand', 'Marca'],
                    ['show_sku', 'SKU'],
                    ['show_price', 'Precio'],
                    ['show_stock', 'Existencias'],
                    ['show_promo', 'Promoción']
                  ].map(([k, lbl]) => (
                    <label key={k} className="flex items-center gap-1">
                      <input type="checkbox" checked={fields[k]} onChange={(e) => setFields({ ...fields, [k]: e.target.checked })} />
                      {lbl}
                    </label>
                  ))}
                </div>
                <div className="mt-2">
                  <Label className="text-xs">Nivel de precio</Label>
                  <Select value={fields.price_field} onValueChange={(v) => setFields({ ...fields, price_field: v })}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="precio_1">Precio 1 (público)</SelectItem>
                      <SelectItem value="precio_2">Precio 2</SelectItem>
                      <SelectItem value="precio_3">Precio 3</SelectItem>
                      <SelectItem value="precio_4">Precio 4</SelectItem>
                      <SelectItem value="precio_5">Precio 5</SelectItem>
                      <SelectItem value="medio_mayoreo">Medio mayoreo</SelectItem>
                      <SelectItem value="mayoreo">Mayoreo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Generación */}
            <div className="lg:col-span-4 bg-white border rounded p-4 space-y-3">
              <h3 className="font-semibold text-lg">Generar</h3>
              <Button onClick={previewHTML} disabled={generating} variant="outline" className="w-full" data-testid="cat-preview">
                <Eye size={18} weight="bold" className="mr-2" /> Vista previa (HTML)
              </Button>
              <Button onClick={() => downloadFile('pdf')} disabled={generating} className="w-full bg-rose-600 hover:bg-rose-700" data-testid="cat-pdf">
                <FilePdf size={18} weight="bold" className="mr-2" /> Descargar PDF
              </Button>
              <Button onClick={() => downloadFile('html')} disabled={generating} className="w-full" variant="outline" data-testid="cat-html">
                <FileHtml size={18} weight="bold" className="mr-2" /> Descargar HTML
              </Button>
              <Button onClick={() => downloadFile('png')} disabled={generating} className="w-full bg-violet-600 hover:bg-violet-700" data-testid="cat-png">
                <FileImage size={18} weight="bold" className="mr-2" /> Descargar PNG
              </Button>
              <div className="border-t pt-3 mt-1 space-y-2">
                <Button
                  onClick={openTelegramDialog}
                  disabled={generating}
                  className="w-full bg-sky-600 hover:bg-sky-700"
                  data-testid="cat-send-telegram"
                >
                  <PaperPlaneTilt size={18} weight="bold" className="mr-2" /> Enviar catálogo por Telegram
                </Button>
                <Button
                  onClick={openStockDialog}
                  disabled={generating}
                  variant="outline"
                  className="w-full border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                  data-testid="cat-send-stock"
                >
                  <PaperPlaneTilt size={18} weight="bold" className="mr-2" /> Enviar existencias
                </Button>
                <p className="text-xs text-muted-foreground">
                  {linkedSellers.length > 0
                    ? `${linkedSellers.length} vendedor${linkedSellers.length === 1 ? '' : 'es'} con Telegram vinculado`
                    : 'Sin vendedores con Telegram vinculado'}
                </p>
              </div>
              <p className="text-xs text-muted-foreground pt-2 border-t">
                💡 PNG con varias páginas se descarga como ZIP. HTML abre en el navegador para imprimir o guardar como PDF.
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-white border rounded p-6 max-w-2xl">
            <h3 className="font-semibold text-lg mb-4">Plantilla del catálogo</h3>
            <div className="space-y-3">
              <div>
                <Label>Título del catálogo</Label>
                <Input value={tpl.title || ''} onChange={(e) => setTpl({ ...tpl, title: e.target.value })} />
              </div>
              <div>
                <Label>Pie de página</Label>
                <Textarea rows={2} value={tpl.footer_text || ''} onChange={(e) => setTpl({ ...tpl, footer_text: e.target.value })} />
              </div>
              <div>
                <Label>Logo</Label>
                <input type="file" accept="image/*,.heic,.heif" onChange={handleLogoUpload} className="text-sm" />
                {tpl.logo_url && (
                  <div className="mt-2 flex items-center gap-2">
                    <img src={tpl.logo_url} alt="logo" className="h-12 border rounded" />
                    <Button size="sm" variant="ghost" onClick={() => setTpl({ ...tpl, logo_url: '' })}>Quitar</Button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Color primario</Label>
                  <div className="flex gap-2">
                    <input type="color" value={tpl.primary_color} onChange={(e) => setTpl({ ...tpl, primary_color: e.target.value })} className="h-10 w-16" />
                    <Input value={tpl.primary_color} onChange={(e) => setTpl({ ...tpl, primary_color: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label>Color secundario</Label>
                  <div className="flex gap-2">
                    <input type="color" value={tpl.secondary_color} onChange={(e) => setTpl({ ...tpl, secondary_color: e.target.value })} className="h-10 w-16" />
                    <Input value={tpl.secondary_color} onChange={(e) => setTpl({ ...tpl, secondary_color: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label>Fondo</Label>
                  <div className="flex gap-2">
                    <input type="color" value={tpl.background_color} onChange={(e) => setTpl({ ...tpl, background_color: e.target.value })} className="h-10 w-16" />
                    <Input value={tpl.background_color} onChange={(e) => setTpl({ ...tpl, background_color: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label>Texto</Label>
                  <div className="flex gap-2">
                    <input type="color" value={tpl.text_color} onChange={(e) => setTpl({ ...tpl, text_color: e.target.value })} className="h-10 w-16" />
                    <Input value={tpl.text_color} onChange={(e) => setTpl({ ...tpl, text_color: e.target.value })} />
                  </div>
                </div>
              </div>
              <Button onClick={saveTemplate} className="w-full">Guardar plantilla</Button>
            </div>
          </div>
        )}
      </div>

      <Dialog open={tgOpen} onOpenChange={setTgOpen}>
        <DialogContent className="max-w-md" data-testid="tg-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PaperPlaneTilt size={22} weight="bold" className="text-sky-600" />
              Enviar catálogo por Telegram
            </DialogTitle>
            <DialogDescription>
              El archivo se generará con los filtros y diseño actuales y se enviará al vendedor.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label className="text-xs">Vendedor</Label>
              <Select value={tgSellerId} onValueChange={setTgSellerId}>
                <SelectTrigger data-testid="tg-seller"><SelectValue placeholder="Selecciona un vendedor…" /></SelectTrigger>
                <SelectContent>
                  {linkedSellers.map(s => (
                    <SelectItem key={s.id} value={s.id} data-testid={`tg-seller-${s.id}`}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">Formato</Label>
              <Select value={tgFormat} onValueChange={setTgFormat}>
                <SelectTrigger data-testid="tg-format"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pdf">PDF (recomendado)</SelectItem>
                  <SelectItem value="png">PNG / ZIP de imágenes</SelectItem>
                  <SelectItem value="html">HTML</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">Mensaje opcional</Label>
              <Textarea
                rows={2}
                placeholder="Ej. Catálogo actualizado de promoción de fin de mes"
                value={tgMessage}
                onChange={(e) => setTgMessage(e.target.value)}
                data-testid="tg-message"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setTgOpen(false)} disabled={tgSending}>Cancelar</Button>
            <Button
              onClick={sendCatalogTelegram}
              disabled={tgSending || !tgSellerId}
              className="bg-sky-600 hover:bg-sky-700"
              data-testid="tg-send"
            >
              <PaperPlaneTilt size={16} weight="bold" className="mr-2" />
              {tgSending ? 'Enviando…' : 'Enviar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={stOpen} onOpenChange={setStOpen}>
        <DialogContent className="max-w-md" data-testid="st-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PaperPlaneTilt size={22} weight="bold" className="text-emerald-600" />
              Enviar lista de existencias
            </DialogTitle>
            <DialogDescription>
              Se enviará un mensaje de Telegram con los productos (nombre + cantidad)
              agrupados por subcategoría, usando los filtros actuales.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label className="text-xs">Vendedor</Label>
              <Select value={stSellerId} onValueChange={setStSellerId}>
                <SelectTrigger data-testid="st-seller"><SelectValue placeholder="Selecciona un vendedor…" /></SelectTrigger>
                <SelectContent>
                  {linkedSellers.map(s => (
                    <SelectItem key={s.id} value={s.id} data-testid={`st-seller-${s.id}`}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">Mensaje opcional</Label>
              <Textarea
                rows={2}
                placeholder="Ej. Existencias del cierre de mes"
                value={stMessage}
                onChange={(e) => setStMessage(e.target.value)}
                data-testid="st-message"
              />
            </div>

            <p className="text-xs text-muted-foreground bg-emerald-50 border border-emerald-200 rounded p-2">
              💡 Si la lista supera el límite de Telegram (4000 caracteres), se envía como archivo .txt automáticamente.
            </p>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setStOpen(false)} disabled={stSending}>Cancelar</Button>
            <Button
              onClick={sendStockTelegram}
              disabled={stSending || !stSellerId}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="st-send"
            >
              <PaperPlaneTilt size={16} weight="bold" className="mr-2" />
              {stSending ? 'Enviando…' : 'Enviar existencias'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};
